import { Component, Input, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { HasAccessService } from '@client/app/pipe/has-access/has-access.service';
import { ComponentDetailsViewService } from '@client/app/blocks/service/component-details-view.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { PageDetailsComponent } from '@client/app/components/pagedetails/details/page-details.component';
import { safeClone } from '@client/app/shared/utils/object-utils';
import { DataPrivacyViolationRepoService } from '@client/app/modules/data-privacy/services/api/data-privacy-violation-repo/data-privacy-violation-repo.service';

@Component({
    selector: 'notification-dropdown',
    standalone: false,
    template: `
        <div class="inline-block notification-dropdown" ngbDropdown display="static">
            <button [disabled]="notificationStackCount() === 0" class="btn btn-border-radius btn-secondary" ngbDropdownToggle>
                {{ notificationStackCount() }}
            </button>
            <ul class="dropdown-menu pull-right border-pointer inner-r inner-l inner-t-medium inner-b-medium" ngbDropdownMenu>
                <li>
                    <div class="text-nowrap inline-block col-middle">
                        <p class="h4 font-weight-300 pull-left outer-t-smurf outer-b-none" translate>Quick info here</p>
                        <span *ngIf="pageDetailsIsAvailable && page.type === 'html'"
                              ngbTooltip="{{ 'Open page details' | translate }}"
                              (click)="openPageDetails()"
                              [triggers]="'focus hover'"
                              class="pt-3 pull-right cursor-pointer text-link page-details"
                              role="button">
                        </span>
                        <span *ngIf="pageDetailsIsAvailable && page.type !== 'html'"
                              ngbTooltip="{{ 'Open document details' | translate }}"
                              (click)="openDocumentDetails()"
                              [triggers]="'focus hover'"
                              class="pt-3 pull-right cursor-pointer text-link page-details"
                              role="button">
                        </span>
                        <div class="clear"></div>
                    </div>
                </li>
                <li class="divider"></li>

                <ng-container *ngIf="!pageDetailsIsAvailable">
                    <li *ngFor="let stack of this.stack"
                        class="text-nowrap outer-t-small"
                        [ngClass]="{'text-grey': stack.attrCount === 0, 'text-danger': stack.attrCount > 0 }">
                        <div class="text-nowrap inline-block">
                            <div class="pull-left">
                                <div class="inline-block icon-width">
                                    <span class="{{ stack.icon }} inner-r-small"></span>
                                </div>
                                <span>{{ stack.name }}</span>
                            </div>
                            <div class="pull-right">
                                <span>{{ stack.attrCount }}</span>
                            </div>
                        </div>
                    </li>
                </ng-container>

                <ng-container *ngIf="pageDetailsIsAvailable && page.type === 'html'">
                    <li (click)="stack.attrCount > 0 && openPageDetails(stack)"
                        [ngClass]="{'text-grey': stack.attrCount === 0, 'text-danger': stack.attrCount > 0 }"
                        *ngFor="let stack of this.stack"
                        class="text-nowrap outer-t-small cursor-pointer">
                        <div class="text-nowrap inline-block">
                            <div class="pull-left">
                                <div class="inline-block icon-width">
                                    <span class="{{ stack.icon }} inner-r-small"></span>
                                </div>
                                <span>{{ stack.name }}</span>
                            </div>
                            <div class="pull-right">
                                <span>{{ stack.attrCount }}</span>
                            </div>
                        </div>
                    </li>
                </ng-container>

                <ng-container *ngIf="pageDetailsIsAvailable && page.type !== 'html'">
                    <li (click)="s.attrCount > 0 && openDocumentDetails(s)"
                        [ngClass]="{'text-grey': s.attrCount === 0, 'text-danger': s.attrCount > 0 }"
                        *ngFor="let s of this.stack"
                        class="text-nowrap outer-t-small cursor-pointer">
                        <div class="text-nowrap inline-block">
                            <div class="pull-left">
                                <div class="inline-block icon-width">
                                    <span class="{{ s.icon }} inner-r-small"></span>
                                </div>
                                <span>{{ s.name }}</span>
                            </div>
                            <div class="pull-right">
                                <span>{{ s.attrCount }}</span>
                            </div>
                        </div>
                    </li>
                </ng-container>
            </ul>
        </div>
    `,
})
export class NotificationDropdownComponent implements OnInit {
    @Input() page: any;
    @Input() showCategories: string[] = [];

    stack: any[] = [];
    blackList: string[] = [];
    pageDetailsIsAvailable: boolean = false;
    user: any;
    dataPrivacyCount: number = 0;

    constructor (
        private monEventService: MonEventService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private loginService: LoginService,
        private activeFeatureService: ActiveFeatureService,
        private hasAccessService: HasAccessService,
        private pipeService: PipeService,
        private componentDetailsView: ComponentDetailsViewService,
        private dataPrivacyViolationRepoService: DataPrivacyViolationRepoService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.stack = [];
        this.showCategories = this.showCategories || [];
        this.blackList = [];
        this.pageDetailsIsAvailable = this.activeFeatureService.isFeatureActive('page_details');
        const hasDataPrivacy = this.activeFeatureService.isFeatureActive(MON_MODULES.data_privacy, this.sessionService.domain ?? undefined);
        const promises: [Promise<any>, Promise<any>?] = hasDataPrivacy
            ? [this.loginService.getUser(), this.dataPrivacyViolationRepoService.getAll(this.page?.id, { status: 'active' })]
            : [this.loginService.getUser()];
        Promise.all(promises).then(([user, violations]) => {
            this.user = user;
            this.dataPrivacyCount = violations?.total ?? 0;
            this.setCategories();
        });
    }

    private setCategories (): void {
        const stack = [
            {
                attr: ['policy_violation_count', 'policy_required_count', 'policy_search_count'],
                name: this.translateService.getString('Content Policies'),
                icon: 'fas fa-gavel',
                target: 'policy',
                attrCount: this.countAttributes(['policy_violation_count', 'policy_required_count', 'policy_search_count']),
                component: 'page-details-section-policies',
            },
            {
                attr: 'dead_links_count',
                name: this.translateService.getString('Broken Links'),
                icon: 'fas fa-unlink',
                target: 'dead-links',
                attrCount: this.countAttributes('dead_links_count'),
                component: 'page-details-section-qa',
                subComponent: 'brokenlinks',
            },
            {
                attr: 'dead_images_count',
                name: this.translateService.getString('Broken Images'),
                icon: 'fas fa-file-image',
                target: 'broken-images',
                attrCount: this.countAttributes('dead_images_count'),
                component: 'page-details-section-qa',
                subComponent: 'brokenimages',
            },
            {
                attr: 'spelling_errors_confirmed_count',
                name: this.translateService.getString('Misspellings'),
                icon: 'fas fa-language',
                target: 'misspellings',
                attrCount: this.countAttributes('spelling_errors_confirmed_count'),
                component: 'page-details-section-qa',
                subComponent: 'misspellings',
            },
            {
                attr: 'accessibility_checks_with_errors_count',
                name: this.translateService.getString('Accessibility'),
                icon: 'fas fa-universal-access',
                target: 'accessibility',
                attrCount: this.countAttributes('accessibility_checks_with_errors_count'),
                component: 'page-details-section-accessibility',
            },
            {
                attr: 'seo_issues_count',
                name: this.translateService.getString('SEO Issues'),
                icon: 'fas fa-chart-line',
                target: 'seo',
                attrCount: this.countAttributes('seo_issues_count'),
                component: 'page-details-section-seo',
            },
            {
                attr: 'data_protection_violations_counts.active_violations',
                name: this.translateService.getString('Data privacy'),
                icon: 'fas fa-lock-alt',
                target: 'data-privacy',
                attrCount: this.dataPrivacyCount,
                component: 'page-details-section-data-privacy',
            },
        ];

        this.removeAccessToStackItems();
        this.setStack(stack);
    }

    private setStack (stack: any[]): void {
        stack = stack.filter((data) => {
            return this.blackList.indexOf(data.target) === -1;
        });

        if (this.showCategories.length === 0) {
            this.stack = stack;
        } else {
            this.stack = stack.filter((data) => {
                return this.showCategories.indexOf(data.target) > -1;
            });
        }
    }

    openPageDetails (target?: any): void {
        target = target || null;
        const data: any = {
            page: this.page,
        };

        if (target && typeof target === 'object') {
            if (target.component !== undefined) {
                data.target = target.component;
            }

            if (target.subComponent !== undefined) {
                data.subTarget = target.subComponent;
            }
        }

        const params = {
            body: PageDetailsComponent,
            size: 'page-details',
            classes: 'page-details-app',
            data: data,
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    openDocumentDetails (target?: any): void {
        target = target || null;
        const params: any = {};

        if (target && typeof target === 'object') {
            if (target.component !== undefined) {
                params.target = target.component;
            }
        }

        this.componentDetailsView.goToDocumentDetails(this.page, params);
    }

    notificationStackCount (): number {
        return this.calculateAllowedStack();
    }

    private calculateAllowedStack (): number {
        let count = 0;
        for (let i = 0; i < this.stack.length; i++) {
            count += this.stack[i].attrCount;
        }
        return count;
    }

    countAttributes (attr: string | string[]): number {
        let result = this.propAccessor(attr);
        if (Array.isArray(attr)) {
            result = attr.reduce((num, attribute) => {
                return num + (this.propAccessor(attribute) || 0);
            }, 0);
        }
        return result;
    }

    private propAccessor (attr: string | string[]): number {
        if (typeof attr === 'string' && attr.indexOf('.') > -1) {
            const properties = attr.split('.');
            let propAccessor: any = safeClone(this.page);
            properties.forEach((property) => {
                if (propAccessor !== null && propAccessor !== undefined) {
                    propAccessor = propAccessor[property] || 0;
                }
            });
            if (isNaN(propAccessor)) {
                return 0;
            } else {
                return propAccessor;
            }
        }
        return this.page[attr as string] || 0;
    }

    private removeAccessToStackItems (): void {
        // Insert conditions here if a component are not allowed for a domain.
        if (
            !this.pipeService.get('activeFeature')('global_policy') ||
            !this.hasAccessService.hasAccess(this.user, MON_MODULES.policies) ||
            !this.activeFeatureService.isFeatureActivePure('policies_all')
        ) {
            this.insertToBlackList('policy');
        }

        if (!this.hasAccessService.hasAccess(this.user, MON_MODULES.qa) || !this.activeFeatureService.isFeatureActivePure('qa_links')) {
            this.insertToBlackList('dead-links');
            this.insertToBlackList('broken-images');
        }
        if (!this.hasAccessService.hasAccess(this.user, MON_MODULES.qa) || !this.activeFeatureService.isFeatureActivePure('qa_spellcheck')) {
            this.insertToBlackList('misspellings');
        }

        if (!this.hasAccessService.hasAccess(this.user, MON_MODULES.accessibility)) {
            this.insertToBlackList('accessibility');
        }

        if (!this.hasAccessService.hasAccess(this.user, MON_MODULES.seo) || !this.activeFeatureService.isFeatureActivePure('seo_all')) {
            this.insertToBlackList('seo');
        }

        if (!this.activeFeatureService.isFeatureActive(MON_MODULES.data_privacy, this.sessionService.domain ?? undefined)) {
            this.insertToBlackList('data-privacy');
        }
    }

    private insertToBlackList (item: string): void {
        if (this.blackList.indexOf(item) === -1) {
            this.blackList.push(item);
        }
    }
}
